/**
 * useChat — manages chat state including tool-use events and confirmation.
 */

import { useCallback, useReducer, useRef } from "react";

import { confirmToolCall, streamChat } from "../api/chatApi";
import type {
  Card,
  ChatErrorKind,
  ChatMessage,
  ChatState,
  ConfirmationRequest,
  SentChip,
  ToolCall,
  ToolResultInfo,
} from "../types/chat";

function newId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

type Action =
  | { type: "USER_MESSAGE"; message: ChatMessage }
  | { type: "ASSISTANT_START"; id: string }
  | { type: "ASSISTANT_TOKEN"; id: string; delta: string }
  | { type: "ASSISTANT_DONE" }
  | { type: "TOOL_START"; id: string; tool: ToolCall }
  | { type: "TOOL_RESULT"; id: string; result: ToolResultInfo }
  | { type: "CONFIRMATION"; id: string; confirmation: ConfirmationRequest }
  | { type: "CARD"; id: string; card: Card }
  | { type: "CONFIRM_ACTION"; toolName: string }
  | { type: "TOOL_CONFIRM_DONE"; id: string; name: string; output: string; isError: boolean }
  | { type: "REJECT_ACTION" }
  | { type: "ERROR"; message: string; id?: string; kind?: ChatErrorKind }
  | { type: "STOP" }
  | { type: "DROP"; id: string }
  | { type: "RETRY" }
  | { type: "NEW_CHAT"; sessionId: string }
  | { type: "HYDRATE"; messages: ChatMessage[] };

function initialState(): ChatState {
  return {
    messages: [],
    status: "idle",
    errorMessage: null,
    sessionId: newId(),
    confirmedTools: [],
  };
}

function reducer(state: ChatState, action: Action): ChatState {
  switch (action.type) {
    case "USER_MESSAGE":
      return { ...state, messages: [...state.messages, action.message], status: "streaming", errorMessage: null, confirmedTools: [] };

    case "ASSISTANT_START":
      return {
        ...state,
        status: "streaming",
        messages: [
          ...state.messages,
          { id: action.id, role: "assistant", content: "", isStreaming: true, timestamp: Date.now(), toolCalls: [], toolResults: [] },
        ],
      };

    case "ASSISTANT_TOKEN":
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.id ? { ...m, content: m.content + action.delta } : m
        ),
      };

    case "TOOL_START":
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.id
            ? { ...m, toolCalls: [...(m.toolCalls ?? []), action.tool] }
            : m
        ),
      };

    case "TOOL_RESULT":
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.id
            ? { ...m, toolResults: [...(m.toolResults ?? []), action.result] }
            : m
        ),
      };

    case "CARD":
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.id
            ? { ...m, cards: [...(m.cards ?? []), action.card] }
            : m
        ),
      };

    case "CONFIRMATION":
      return {
        ...state,
        status: "awaiting_confirmation",
        messages: state.messages.map((m) =>
          m.id === action.id ? { ...m, confirmation: action.confirmation, isStreaming: false } : m
        ),
      };

    case "CONFIRM_ACTION":
      return {
        ...state,
        status: "streaming",
        confirmedTools: [...state.confirmedTools, action.toolName],
        messages: state.messages.map((m) =>
          m.confirmation ? { ...m, confirmation: null, isStreaming: true } : m
        ),
      };

    case "TOOL_CONFIRM_DONE":
      return {
        ...state,
        status: action.isError ? "error" : "idle",
        errorMessage: action.isError ? action.output : null,
        messages: state.messages.map((m) =>
          m.id === action.id
            ? {
                ...m,
                isStreaming: false,
                toolResults: [...(m.toolResults ?? []), { name: action.name, output: action.output }],
                content: m.content + (m.content ? "\n\n" : "") + action.output,
              }
            : m
        ),
      };

    case "REJECT_ACTION":
      return {
        ...state,
        status: "idle",
        messages: state.messages.map((m) =>
          m.confirmation ? { ...m, confirmation: null, isStreaming: false, content: m.content + "\n\n*Action cancelled by user.*" } : m
        ),
      };

    case "ASSISTANT_DONE":
      return {
        ...state,
        status: "idle",
        messages: state.messages.map((m) => (m.isStreaming ? { ...m, isStreaming: false } : m)),
      };

    case "ERROR":
      return {
        ...state,
        status: "error",
        errorMessage: action.message,
        errorKind: action.kind ?? "server",
        messages: state.messages.map((m) =>
          m.id === action.id
            ? { ...m, isStreaming: false, errorKind: action.kind ?? "server", errorText: action.message }
            : m.isStreaming
              ? { ...m, isStreaming: false }
              : m,
        ),
      };

    case "STOP":
      return {
        ...state,
        status: "idle",
        messages: state.messages.map((m) => (m.isStreaming ? { ...m, isStreaming: false, stopped: true } : m)),
      };

    case "DROP":
      return { ...state, messages: state.messages.filter((m) => m.id !== action.id) };

    case "RETRY":
      return { ...state, status: "idle", errorMessage: null, errorKind: null };

    case "NEW_CHAT":
      return { ...initialState(), sessionId: action.sessionId };

    case "HYDRATE":
      // Never clobber an in-flight or already-populated thread — hydration
      // races with the ?prompt= auto-send on brand-new sessions.
      if (state.status === "streaming" || state.messages.length > 0) return state;
      return { ...state, messages: action.messages };

    default:
      return state;
  }
}

export interface TtsStreamController {
  onAssistantStart(messageId: string): void;
  onAssistantToken(messageId: string, delta: string): void;
  onAssistantDone(messageId: string): void;
  onAbort(): void;
}

export function useChat(opts?: { tts?: TtsStreamController }) {
  const tts = opts?.tts;
  const [state, dispatch] = useReducer(reducer, undefined, initialState);
  const abortRef = useRef<AbortController | null>(null);

  const sendMessage = useCallback(async (
    text: string,
    confirmedTools?: string[],
    attachmentId?: string,
    opts?: { chips?: SentChip[]; skipUserMessage?: boolean },
  ) => {
    if (!text.trim() || state.status === "streaming") return;

    abortRef.current?.abort();
    tts?.onAbort();
    const controller = new AbortController();
    abortRef.current = controller;

    // Only add user message on fresh sends (not confirmation resends)
    if (!confirmedTools?.length && !opts?.skipUserMessage) {
      const userMsg: ChatMessage = {
        id: newId(),
        role: "user",
        content: text.trim(),
        timestamp: Date.now(),
        ...(opts?.chips?.length ? { chips: opts.chips } : {}),
      };
      dispatch({ type: "USER_MESSAGE", message: userMsg });
    }

    const assistantId = newId();
    dispatch({ type: "ASSISTANT_START", id: assistantId });
    tts?.onAssistantStart(assistantId);

    try {
      await streamChat({
        message: text.trim(),
        sessionId: state.sessionId,
        confirmedToolCalls: confirmedTools ?? state.confirmedTools,
        attachmentId,
        signal: controller.signal,
        onToken: (delta) => {
          dispatch({ type: "ASSISTANT_TOKEN", id: assistantId, delta });
          tts?.onAssistantToken(assistantId, delta);
        },
        onDone: () => {
          dispatch({ type: "ASSISTANT_DONE" });
          tts?.onAssistantDone(assistantId);
          window.dispatchEvent(new CustomEvent("weco:assistant:session-updated"));
        },
        onError: (msg, kind) => dispatch({ type: "ERROR", message: msg, id: assistantId, kind }),
        onToolStart: (name, input) => dispatch({ type: "TOOL_START", id: assistantId, tool: { name, input } }),
        onToolResult: (name, output) => dispatch({ type: "TOOL_RESULT", id: assistantId, result: { name, output } }),
        onCard: (card) => dispatch({ type: "CARD", id: assistantId, card }),
        onConfirmation: (toolName, toolId, input) =>
          dispatch({ type: "CONFIRMATION", id: assistantId, confirmation: { tool_name: toolName, tool_id: toolId, input } }),
      });
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
      const offline = typeof navigator !== "undefined" && navigator.onLine === false;
      dispatch({
        type: "ERROR",
        message: "Connection lost. Please try again.",
        id: assistantId,
        kind: offline ? "offline" : "network",
      });
    }
  }, [state.status, state.sessionId, state.confirmedTools, tts]);

  const confirmAction = useCallback(async (id: string, toolId: string, toolName: string) => {
    dispatch({ type: "CONFIRM_ACTION", toolName });
    try {
      const { tool_name, output, is_error } = await confirmToolCall(state.sessionId, toolId);
      dispatch({ type: "TOOL_CONFIRM_DONE", id, name: tool_name, output, isError: is_error });
      window.dispatchEvent(new CustomEvent("weco:assistant:session-updated"));
    } catch {
      dispatch({ type: "TOOL_CONFIRM_DONE", id, name: toolName, output: "Could not complete the action. Please try again.", isError: true });
    }
  }, [state.sessionId]);

  const rejectAction = useCallback(() => {
    dispatch({ type: "REJECT_ACTION" });
  }, []);

  const newChat = useCallback((sessionId?: string) => {
    abortRef.current?.abort();
    tts?.onAbort();
    dispatch({ type: "NEW_CHAT", sessionId: sessionId ?? newId() });
  }, [tts]);

  const retry = useCallback(() => {
    dispatch({ type: "RETRY" });
  }, []);

  const hydrateMessages = useCallback((messages: ChatMessage[]) => {
    dispatch({ type: "HYDRATE", messages });
  }, []);

  /**
   * Stop the reply being streamed. Only the client stops listening: the
   * backend keeps its one-reply-in-flight lock until its own worker finishes,
   * so a send right after Stop can come back "busy" (409), which the Hub
   * explains instead of failing silently.
   */
  const stop = useCallback(() => {
    if (!abortRef.current) return;
    abortRef.current.abort();
    abortRef.current = null;
    tts?.onAbort();
    dispatch({ type: "STOP" });
  }, [tts]);

  /** Re-ask a failed reply in place: drop the failed bubble, resend the prompt without a second user bubble. */
  const retryReply = useCallback((assistantId: string) => {
    const idx = state.messages.findIndex((m) => m.id === assistantId);
    const prompt = [...state.messages.slice(0, idx < 0 ? state.messages.length : idx)]
      .reverse()
      .find((m) => m.role === "user");
    if (!prompt) return;
    dispatch({ type: "RETRY" });
    dispatch({ type: "DROP", id: assistantId });
    void sendMessage(prompt.content, undefined, undefined, { skipUserMessage: true });
  }, [state.messages, sendMessage]);

  return { ...state, sendMessage, newChat, retry, retryReply, stop, confirmAction, rejectAction, hydrateMessages };
}
